/**
 * Which view of the Team page is showing, and whether the advisor may choose another.
 *
 * Normally the view comes from the `?view=` query parameter, falling back to the page's computed
 * default. An advisor whose pilot program withholds the Team page but who carries
 * `pilotTeamOwnerView` (the pilot's designated inviter, see `getPilotTeamAccess`) gets the Owner
 * view only: the Member and Invitations views are not offered, and a `?view=member` or
 * `?view=invitations` link (the invitation emails use the latter) is rewritten to `owner` instead
 * of being honoured.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePilotTeamAccess } from '@/shared/hooks/use-active-pilot-program';

export type TeamView = 'owner' | 'member' | 'invitations';

export function useTeamPageView(defaultView: TeamView) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isOwnerOnly = usePilotTeamAccess() === 'owner-only';

  const requestedView = searchParams.get('view') as TeamView | null;
  const view: TeamView = isOwnerOnly ? 'owner' : requestedView || defaultView;

  const setView = (newView: TeamView) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', newView);
    setSearchParams(newParams, { replace: true });
  };

  useEffect(() => {
    if (isOwnerOnly && requestedView && requestedView !== 'owner') {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('view', 'owner');
      setSearchParams(newParams, { replace: true });
    }
  }, [isOwnerOnly, requestedView, searchParams, setSearchParams]);

  return { view, setView, isOwnerOnly };
}
